import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { setCurrentScreen, clearCart, setLoyaltyCode, setOrderMetadata, setCurrentOrderUnpaid, submitCart } from '../../store/orderSlice';
import apiService from '../../services/apiService';
import PanarottisIcon from '../../data/PanaImages/PanarottisIcon.png';
import CardPaymentImage from '../../data/PanaImages/CardPayment.png';

const POLL_INTERVAL_MS = 5000;

const nexaRustStack = '"Nexa Rust Sans Black A", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';

function getLatestPaymentStatus(history: { status: string }[]): string | null {
  if (!history || history.length === 0) return null;
  return history[history.length - 1].status;
}

function isPaymentSuccessful(status: string | null): boolean {
  return status === 'Completed' || status === 'Paid' || status === 'Authorised';
}

function isPaymentFailed(status: string | null): boolean {
  return status === 'TimedOut' || status === 'Cancelled' || status === 'Failed';
}

function orderNumberFromOrderId(orderId: string): string {
  return orderId.slice(-5).toUpperCase();
}

const PayHereScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const paymentId = useSelector((state: RootState) => state.order.paymentId);
  const cartId = useSelector((state: RootState) => state.order.cartId);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const doStartOver = useCallback(() => {
    dispatch(clearCart());
    dispatch(setLoyaltyCode(null));
    dispatch(setCurrentScreen('welcome'));
  }, [dispatch]);

  useEffect(() => {
    if (!paymentId || !cartId) {
      doStartOver();
      return;
    }

    const stopPolling = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };

    const checkStatus = async () => {
      try {
        const res = await apiService.getPaymentStatus(paymentId);
        const status = getLatestPaymentStatus(res.data?.history ?? []);

        if (isPaymentSuccessful(status)) {
          stopPolling();
          const { orderId } = await dispatch(submitCart(cartId)).unwrap();
          dispatch(setOrderMetadata({ orderNumber: orderNumberFromOrderId(orderId), timestamp: new Date().toISOString() }));
          dispatch(setCurrentOrderUnpaid(false));
          dispatch(setCurrentScreen('success'));
          return;
        }

        if (isPaymentFailed(status)) {
          stopPolling();
          dispatch(setCurrentScreen('payment'));
        }
      } catch {
        // On fetch error, keep polling indefinitely
      }
    };

    checkStatus();
    intervalRef.current = setInterval(checkStatus, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [paymentId, cartId, dispatch, doStartOver]);

  return (
    <div className="min-h-screen bg-[#f2f1ec] flex flex-col items-center px-10 pt-12 pb-12">
      <img
        src={PanarottisIcon}
        alt="Panarottis"
        className="w-[100px] h-[75.76px] object-contain"
      />

      <h1
        className="mt-12 text-center uppercase text-[#185C53]"
        style={{
          fontFamily: nexaRustStack,
          fontSize: '80px',
          lineHeight: '88px',
          fontWeight: 900,
          letterSpacing: '2px',
        }}
      >
        Insert or Tap Your Card
      </h1>

      <img
        src={CardPaymentImage}
        alt="Insert or tap card"
        className="mt-20 w-[420px] h-[465.16px] object-contain"
      />
    </div>
  );
};

export default PayHereScreen;
